import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Page from 'src/components/Page';
import { downloadTextFile, formatMoney, payrollImportService } from 'src/services/payroll';
import type { TaxYtdRow, TaxTransactionDetail, PayrollSnapshot } from 'src/types/payroll.types';
import { PaycheckStub } from './components/PaycheckStub';

const currentYear = new Date().getFullYear();
const years = [currentYear, currentYear - 1, currentYear - 2];

function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
}

export default function TaxDayExport() {
  const navigate = useNavigate();
  const [year, setYear] = useState(currentYear);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set());
  const [selectedStub, setSelectedStub] = useState<{
    snapshot: PayrollSnapshot;
    studentName: string;
    amount: number;
    createdAt: string;
  } | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['payroll-tax-ytd', year],
    queryFn: () => payrollImportService.getTaxYtd(year),
  });

  // Collect all transactions across all students
  const allTransactions = useMemo(() => {
    if (!data) return [];
    const list: TaxTransactionDetail[] = [];
    data.forEach((row) => {
      if (row.transactions) {
        list.push(...row.transactions);
      }
    });
    return list.sort(
      (a, b) => new Date(b.dateRecorded).getTime() - new Date(a.dateRecorded).getTime()
    );
  }, [data]);

  // Unique pay periods
  const availablePeriods = useMemo(() => {
    const set = new Set<string>();
    allTransactions.forEach((t) => {
      if (t.payPeriodKey) set.add(t.payPeriodKey);
    });
    return Array.from(set).sort();
  }, [allTransactions]);

  // Totals calculations
  const totals = useMemo(() => {
    const gross = (data || []).reduce((sum, r) => sum + r.ytdGross, 0);
    const tax = (data || []).reduce((sum, r) => sum + r.ytdTaxWithheld, 0);
    const net = (data || []).reduce((sum, r) => sum + r.ytdNet, 0);
    const scholars = (data || []).length;
    const transactionsCount = allTransactions.length;
    const effectiveRate = gross > 0 ? ((tax / gross) * 100).toFixed(1) : '10.0';

    return { gross, tax, net, scholars, transactionsCount, effectiveRate };
  }, [data, allTransactions]);

  // Filtered scholars for Tab 0
  const filteredScholars = useMemo(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();

    return data.filter((row) => {
      const matchesSearch =
        !q ||
        row.firstName.toLowerCase().includes(q) ||
        row.lastName.toLowerCase().includes(q) ||
        (row.externalStudentId && row.externalStudentId.toLowerCase().includes(q)) ||
        row.transactions.some(
          (t) =>
            (t.transactionId && t.transactionId.toLowerCase().includes(q)) ||
            t.payPeriodKey.toLowerCase().includes(q)
        );

      const matchesPeriod =
        periodFilter === 'all' || row.transactions.some((t) => t.payPeriodKey === periodFilter);

      return matchesSearch && matchesPeriod;
    });
  }, [data, searchQuery, periodFilter]);

  // Filtered transactions for Tab 1
  const filteredTransactions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return allTransactions.filter((tx) => {
      const matchesSearch =
        !q ||
        tx.firstName.toLowerCase().includes(q) ||
        tx.lastName.toLowerCase().includes(q) ||
        (tx.externalStudentId && tx.externalStudentId.toLowerCase().includes(q)) ||
        (tx.transactionId && tx.transactionId.toLowerCase().includes(q)) ||
        tx.payPeriodKey.toLowerCase().includes(q);

      const matchesPeriod = periodFilter === 'all' || tx.payPeriodKey === periodFilter;

      return matchesSearch && matchesPeriod;
    });
  }, [allTransactions, searchQuery, periodFilter]);

  const toggleStudentExpanded = (studentId: string) => {
    setExpandedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (expandedStudents.size === filteredScholars.length) {
      setExpandedStudents(new Set());
    } else {
      setExpandedStudents(new Set(filteredScholars.map((s) => s.studentId)));
    }
  };

  const handleExportSummary = () => {
    if (!filteredScholars.length) {
      toast.info('No posted student withholding records for this selection');
      return;
    }
    const csv = payrollImportService.exportTaxYtdCsv(filteredScholars);
    downloadTextFile(`tax-day-ytd-${year}-summary.csv`, csv);
    toast.success(`Exported ${filteredScholars.length} scholar tax summaries`);
  };

  const handleExportTransactions = () => {
    if (!filteredTransactions.length) {
      toast.info('No withholding transactions found for this selection');
      return;
    }
    const csv = payrollImportService.exportTaxTransactionsCsv(filteredTransactions);
    downloadTextFile(`tax-day-audit-ledger-${year}.csv`, csv);
    toast.success(`Exported ${filteredTransactions.length} tax transactions`);
  };

  const handleOpenStub = (tx: TaxTransactionDetail) => {
    const snapshot: PayrollSnapshot = {
      source: 'payroll_import',
      payroll_import_id: tx.payrollImportId,
      pay_period_key: tx.payPeriodKey,
      date_pulled: tx.datePulled,
      first_name: tx.firstName,
      last_name: tx.lastName,
      external_student_id: tx.externalStudentId,
      gross: tx.gross,
      tax_rate: tx.taxRate,
      tax_withheld: tx.taxWithheld,
      net: tx.net,
      periods: tx.periods || {},
      courses: tx.courses,
    };

    setSelectedStub({
      snapshot,
      studentName: `${tx.firstName} ${tx.lastName}`.trim(),
      amount: tx.net,
      createdAt: tx.dateRecorded,
    });
  };

  return (
    <Page title="Tax Day Reconciliation & Withholding Ledger">
      <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1.5, sm: 2.5 }, mx: 0 }}>
        {/* Navigation & Header */}
        <Button
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={() => navigate('/admin/payroll')}
          sx={{ textTransform: 'none', mb: 1, color: 'text.secondary', fontWeight: 600, p: 0 }}
        >
          Scholar Dollar Pay
        </Button>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Chip
                icon={<Icon icon="solar:shield-check-bold" width={16} />}
                label="Tax Consultant & Auditor Portal"
                size="small"
                sx={{
                  bgcolor: alpha('#805AD5', 0.12),
                  color: '#805AD5',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
              <Chip
                label={`Tax Year ${year}`}
                size="small"
                sx={{ bgcolor: '#EDF2F7', color: '#4A5568', fontWeight: 700, fontSize: '0.7rem', height: 24 }}
              />
            </Stack>
            <Typography variant="h4" fontWeight={800} color="#1A202C" sx={{ letterSpacing: -0.5 }}>
              Tax Day Reconciliation & Withholding Ledger
            </Typography>
            <Typography color="text.secondary" maxWidth={680} variant="body2" sx={{ mt: 0.5 }}>
              Audit year-to-date classroom tax withholdings, track the exact date and timestamp when tax was
              deducted per student, and export audit-ready reports for Tax Day and simulated W-2 reconciliations.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              select
              size="small"
              label="Tax Year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ minWidth: 110 }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              startIcon={<Icon icon="mdi:file-table-outline" />}
              onClick={handleExportSummary}
              disabled={!filteredScholars.length}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                borderColor: '#CBD5E0',
                color: '#2D3748',
                '&:hover': { bgcolor: '#F7FAFC', borderColor: '#A0AEC0' },
              }}
            >
              Export Summary CSV
            </Button>

            <Button
              variant="contained"
              startIcon={<Icon icon="mdi:download" />}
              onClick={handleExportTransactions}
              disabled={!filteredTransactions.length}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: '#805AD5',
                '&:hover': { bgcolor: '#6B46C1' },
              }}
            >
              Export Audit Ledger CSV
            </Button>
          </Stack>
        </Stack>

        {/* KPI Reconciliation Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {[
            {
              label: 'Total Tax Withheld',
              value: formatMoney(totals.tax),
              hint: 'Cumulative simulated tax liability',
              color: '#805AD5',
              accent: '#FAF5FF',
            },
            {
              label: 'Total Academic Gross',
              value: formatMoney(totals.gross),
              hint: 'Total student earnings tax base',
              color: '#2B6CB0',
              accent: '#EBF8FF',
            },
            {
              label: 'Total Net Disbursed',
              value: formatMoney(totals.net),
              hint: 'Deposited into scholar checking wallets',
              color: '#297041',
              accent: '#E7F7EF',
            },
            {
              label: 'Participating Scholars',
              value: totals.scholars,
              hint: 'Students with active tax records',
              color: '#D69E2E',
              accent: '#FFFBEB',
            },
            {
              label: 'Effective Tax Rate',
              value: `${totals.effectiveRate}%`,
              hint: 'Classroom statutory withholding',
              color: '#319795',
              accent: '#E6FFFA',
            },
            {
              label: 'Tax Deductions Logged',
              value: totals.transactionsCount,
              hint: 'Itemized payroll withholding entries',
              color: '#4A5568',
              accent: '#F7FAFC',
            },
          ].map((stat) => (
            <Grid item xs={12} sm={6} md={2} key={stat.label}>
              <Card
                elevation={0}
                sx={{
                  p: 1.75,
                  height: '100%',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(stat.color, 0.2),
                  background: `linear-gradient(145deg, ${stat.accent} 0%, #FFFFFF 85%)`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    fontSize: '0.675rem',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography variant="h5" fontWeight={800} color={stat.color} sx={{ lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.675rem' }}>
                  {stat.hint}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filter Bar & Tabs */}
        <Card elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #E2E8F0', mb: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #E2E8F0',
              bgcolor: '#F8FAFC',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { md: 'center' },
              gap: 1.5,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  minHeight: 40,
                  px: 2,
                },
                '& .Mui-selected': {
                  color: '#805AD5',
                },
                '& .MuiTabs-indicator': {
                  bgcolor: '#805AD5',
                },
              }}
            >
              <Tab
                icon={<Icon icon="mdi:account-group-outline" width={18} />}
                iconPosition="start"
                label={`Scholar Summary Ledger (${filteredScholars.length})`}
              />
              <Tab
                icon={<Icon icon="mdi:format-list-bulleted-type" width={18} />}
                iconPosition="start"
                label={`Itemized Transaction Audit Log (${filteredTransactions.length})`}
              />
            </Tabs>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search scholar, ID, or tx…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="mdi:magnify" width={18} color="#718096" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <Icon icon="mdi:close" width={14} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ minWidth: { xs: '100%', sm: 220 } }}
              />

              {availablePeriods.length > 1 && (
                <TextField
                  select
                  size="small"
                  label="Pay Period"
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All Pay Periods</MenuItem>
                  {availablePeriods.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p.replace('_', ' → ')}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {tab === 0 && filteredScholars.length > 0 && (
                <Button
                  size="small"
                  onClick={toggleExpandAll}
                  startIcon={
                    <Icon
                      icon={
                        expandedStudents.size === filteredScholars.length
                          ? 'mdi:collapse-all-outline'
                          : 'mdi:expand-all-outline'
                      }
                    />
                  }
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                  {expandedStudents.size === filteredScholars.length ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
            </Stack>
          </Box>

          {/* Loading or Empty State */}
          {isLoading || isFetching ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: '#805AD5' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Loading year-to-date tax records for {year}…
              </Typography>
            </Box>
          ) : !data?.length ? (
            <Box sx={{ py: 8, textAlign: 'center', px: 2 }}>
              <Icon icon="mdi:file-document-outline" width={48} color="#A0AEC0" />
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1.5 }}>
                No posted Scholar Dollar paychecks for {year} yet
              </Typography>
              <Typography color="text.secondary" variant="body2" maxWidth={480} sx={{ mx: 'auto', mt: 0.5 }}>
                Tax withholdings will appear here automatically once biweekly payroll imports are approved and
                posted.
              </Typography>
              <Button sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }} onClick={() => refetch()}>
                Refresh Records
              </Button>
            </Box>
          ) : tab === 0 ? (
            /* TAB 0: Scholar Summary Ledger with Expandable Rows */
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#F7FAFC', fontSize: '0.75rem', py: 1.25 } }}>
                    <TableCell width={40} />
                    <TableCell>Scholar ID</TableCell>
                    <TableCell>Scholar Name</TableCell>
                    <TableCell align="center">Paychecks</TableCell>
                    <TableCell align="right">Effective Rate</TableCell>
                    <TableCell align="right">YTD Gross</TableCell>
                    <TableCell align="right" sx={{ color: '#805AD5' }}>
                      YTD Tax Withheld
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#297041' }}>
                      YTD Net
                    </TableCell>
                    <TableCell align="center">Audit Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredScholars.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No scholars match the search criteria.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredScholars.map((row) => {
                      const isExpanded = expandedStudents.has(row.studentId);
                      const txCount = row.transactions?.length || 0;

                      return (
                        <React.Fragment key={row.studentId}>
                          <TableRow
                            hover
                            sx={{
                              bgcolor: isExpanded ? alpha('#805AD5', 0.04) : undefined,
                              cursor: 'pointer',
                              '& > td': { borderBottom: isExpanded ? 'none' : undefined },
                            }}
                            onClick={() => toggleStudentExpanded(row.studentId)}
                          >
                            <TableCell sx={{ py: 1 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStudentExpanded(row.studentId);
                                }}
                                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                              >
                                <Icon
                                  icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                                  width={18}
                                  color="#805AD5"
                                />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {row.externalStudentId || '—'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>
                                {row.lastName}, {row.firstName}
                              </Typography>
                              {row.gradeLevel && (
                                <Typography variant="caption" color="text.secondary">
                                  Grade {row.gradeLevel}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${row.paycheckCount} check${row.paycheckCount === 1 ? '' : 's'}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.675rem',
                                  fontWeight: 700,
                                  bgcolor: alpha('#2B6CB0', 0.1),
                                  color: '#2B6CB0',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {row.effectiveTaxRate ? `${row.effectiveTaxRate.toFixed(1)}%` : '10.0%'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatMoney(row.ytdGross)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: '#805AD5' }}>
                              {formatMoney(row.ytdTaxWithheld)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#297041' }}>
                              {formatMoney(row.ytdNet)}
                            </TableCell>
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => toggleStudentExpanded(row.studentId)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: '#805AD5',
                                  py: 0.25,
                                }}
                              >
                                {isExpanded ? 'Hide history' : `View ${txCount} tx`}
                              </Button>
                            </TableCell>
                          </TableRow>

                          {/* Collapsible Nested Transactions Detail Table */}
                          <TableRow>
                            <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #E2E8F0' : 'none' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    py: 1.5,
                                    px: { xs: 2, sm: 4 },
                                    bgcolor: alpha('#805AD5', 0.03),
                                    borderLeft: '4px solid #805AD5',
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                  >
                                    <Typography variant="caption" fontWeight={800} color="#805AD5" textTransform="uppercase">
                                      Itemized Tax Withholding History · {row.firstName} {row.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {txCount} deduction{txCount === 1 ? '' : 's'} recorded
                                    </Typography>
                                  </Stack>

                                  <Table size="small" sx={{ bgcolor: '#FFFFFF', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #EDF2F7' }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: '#F7FAFC' }}>
                                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                          Date & Time Recorded
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                          Pay Period
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                          Transaction ID
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                          Gross Pay
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                          Tax Rate
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#805AD5' }}>
                                          Tax Withheld
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#297041' }}>
                                          Net Deposit
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                          Action
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.transactions?.map((tx) => (
                                        <TableRow key={tx.id} hover>
                                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                              <Icon icon="mdi:calendar-clock" width={14} color="#718096" />
                                              <span>{formatTimestamp(tx.dateRecorded)}</span>
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={{ fontSize: '0.75rem' }}>
                                            {tx.payPeriodKey.replace('_', ' → ')}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#4A5568' }}>
                                            {tx.transactionId ? `#${tx.transactionId.substring(0, 8)}` : '—'}
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                            {formatMoney(tx.gross)}
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                            {(tx.taxRate * 100).toFixed(0)}%
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#805AD5' }}>
                                            {formatMoney(tx.taxWithheld)}
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#297041' }}>
                                            {formatMoney(tx.net)}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Button
                                              size="small"
                                              startIcon={<Icon icon="mdi:receipt-text-outline" width={14} />}
                                              onClick={() => handleOpenStub(tx)}
                                              sx={{
                                                textTransform: 'none',
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                py: 0.2,
                                              }}
                                            >
                                              View Stub
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* TAB 1: Itemized Transaction Audit Log (Flat, chronological) */
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#F7FAFC', fontSize: '0.75rem', py: 1.25 } }}>
                    <TableCell>Date & Time Recorded</TableCell>
                    <TableCell>Scholar Name</TableCell>
                    <TableCell>Scholar ID</TableCell>
                    <TableCell>Pay Period</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell align="right">Gross Pay</TableCell>
                    <TableCell align="right">Tax Rate</TableCell>
                    <TableCell align="right" sx={{ color: '#805AD5' }}>
                      Tax Withheld
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#297041' }}>
                      Net Deposited
                    </TableCell>
                    <TableCell align="center">Audit Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No transactions match the search criteria.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransactions.map((tx) => (
                      <TableRow key={tx.id} hover>
                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Icon icon="mdi:calendar-clock" width={14} color="#718096" />
                            <span>{formatTimestamp(tx.dateRecorded)}</span>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {tx.lastName}, {tx.firstName}
                          </Typography>
                          {tx.gradeLevel && (
                            <Typography variant="caption" color="text.secondary">
                              Grade {tx.gradeLevel}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {tx.externalStudentId || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          {tx.payPeriodKey.replace('_', ' → ')}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', color: '#4A5568', fontSize: '0.75rem' }}>
                          {tx.transactionId ? `#${tx.transactionId.substring(0, 8)}` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                          {formatMoney(tx.gross)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {(tx.taxRate * 100).toFixed(0)}%
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#805AD5' }}>
                          {formatMoney(tx.taxWithheld)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#297041' }}>
                          {formatMoney(tx.net)}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Icon icon="mdi:receipt-text-outline" width={14} />}
                            onClick={() => handleOpenStub(tx)}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              py: 0.2,
                              borderRadius: 1.5,
                              borderColor: '#CBD5E0',
                              color: '#2D3748',
                              '&:hover': { bgcolor: '#F7FAFC' },
                            }}
                          >
                            View Stub
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Paycheck Stub Dialog for Audit Inspections */}
        <Dialog
          open={!!selectedStub}
          onClose={() => setSelectedStub(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {selectedStub && (
            <PaycheckStub
              snapshot={selectedStub.snapshot}
              studentName={selectedStub.studentName}
              transactionAmount={selectedStub.amount}
              createdAt={selectedStub.createdAt}
              onClose={() => setSelectedStub(null)}
            />
          )}
        </Dialog>
      </Container>
    </Page>
  );
}
